import type { PreOrderFlow } from "@/app/api/types/preOrder";
import { cn } from "@/lib/utils";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  focusNextTabCell,
  focusPrevTabCell,
  focusEnterTarget,
} from "../../utils/tabNavigation";
import {
  computeDefaultFlowWeight,
  getJunctionWeight,
  weightsEqual,
} from "../../utils/resolveProductDefaults";
import type { UnitCellProps } from "./types";
import {
  CELL_INPUT_SELECT,
  CELL_INPUT_SELECT_FOCUS,
  CELL_INPUT_DISABLED,
} from "./styles";
import {
  type DropdownPos,
  dropdownPanelStyle,
  useDropdownPosition,
} from "../ui/dropdown-hooks";
import {
  AI_PENDING_CELL_CLASS,
  getAiChangeValues,
  isAiPendingField,
} from "./AiChangeBadge";

const UNIT_DROPDOWN_DESIRED_HEIGHT = 200;
const UNIT_DROPDOWN_MIN_WIDTH = 80;

/**
 * UnitCell — lazy wrapper. Renders a lightweight `<input disabled>`
 * placeholder showing the current unit abbreviation until the user clicks
 * / focuses the cell. The full editor (this whole 300+ line component
 * with its dropdown-position effects, item-list memos, keyboard
 * navigation handlers, and portal-rendered dropdown) only mounts when
 * actually needed. Mount sticks for the row's lifetime — only the first
 * interaction per cell pays the editor mount cost.
 *
 * AI overlay (violet bg + ring + pulse) renders at this outer wrapper so
 * it's visible BEFORE the user activates the cell. When AI is staging a
 * new unit, the idle placeholder shows the staged abbreviation in bold
 * violet (no from→to chrome — the prior unit is omitted by design).
 */
export function UnitCell(props: Readonly<UnitCellProps>) {
  const [activated, setActivated] = useState(false);
  const aiPending = isAiPendingField(props.flow, "unitId");
  const aiChange = getAiChangeValues(props.flow, "unitId");

  // Resolve the staged unit abbreviation from the AI suggestion's `to` so the
  // idle placeholder can render it in place of the current unit.
  const stagedAbbreviation = useMemo<string | null>(() => {
    if (!aiPending || !aiChange) return null;
    const stagedId = aiChange.to;
    if (stagedId == null || stagedId === "") return "—";
    const u = props.availableUnits.find((x) => x.id === stagedId);
    return u?.abbreviation || String(stagedId);
  }, [aiPending, aiChange, props.availableUnits]);

  const inner = !activated ? (
    <UnitCellPlaceholder
      flow={props.flow}
      availableUnits={props.availableUnits}
      disabled={!!props.disabled}
      onActivate={() => setActivated(true)}
      displayOverride={stagedAbbreviation}
      displayClassName={
        stagedAbbreviation != null ? "text-violet-700 font-bold" : undefined
      }
    />
  ) : (
    // `autoFocus` makes the editor's input grab focus on mount — which fires
    // its existing `onFocus` handler, which opens the dropdown. Net effect:
    // a single click / tab on the placeholder lands the user in an open
    // searchable dropdown, matching the pre-lazy behaviour.
    <UnitCellEditor {...props} autoFocus />
  );

  // Always render the same root shape so the editor's React identity stays
  // stable when `aiPending` toggles (e.g. user accepts/declines mid-edit).
  // Otherwise the editor remounts, losing dropdown/search/highlight state
  // and re-firing the autofocus-opens-dropdown effect.
  //
  // Stronger AI cue on UnitCell (per product request): keep the existing
  // violet background tint, add a constant 2px violet ring, and pulse the
  // ring's box-shadow only — text content stays at full opacity (`animate-pulse`
  // would dim the whole cell). Custom keyframe lives in tailwind.config.ts as
  // `ai-pulse`.
  return (
    <div
      data-testid="flow-unit-cell"
      className={
        aiPending
          ? cn(
              AI_PENDING_CELL_CLASS,
              "ring-2 ring-violet-400 ring-offset-1 animate-ai-pulse",
            )
          : undefined
      }
    >
      {inner}
    </div>
  );
}

function UnitCellPlaceholder({
  flow,
  availableUnits,
  disabled,
  onActivate,
  displayOverride,
  displayClassName,
}: Readonly<{
  flow: UnitCellProps["flow"];
  availableUnits: UnitCellProps["availableUnits"];
  disabled: boolean;
  onActivate: () => void;
  displayOverride?: string | null;
  displayClassName?: string;
}>) {
  // Resolve display value the same way the full editor does, but without
  // the dropdown/search state machinery.
  const unitId = flow.unit_id ?? null;
  const resolved =
    (flow.unit?.id === unitId ? flow.unit : null) ??
    availableUnits.find((u) => u.id === unitId) ??
    null;
  const display = displayOverride ?? (resolved?.abbreviation || "-");
  const hasOverride = displayOverride != null;

  return (
    <div className="relative">
      <input
        type="text"
        data-tab-cell
        value={display}
        readOnly
        onFocus={disabled ? undefined : onActivate}
        onClick={disabled ? undefined : onActivate}
        disabled={disabled}
        className={cn(
          CELL_INPUT_SELECT,
          hasOverride
            ? displayClassName
            : resolved?.abbreviation
              ? "text-gray-900"
              : "text-gray-400",
          disabled
            ? CELL_INPUT_DISABLED
            : `cursor-pointer ${CELL_INPUT_SELECT_FOCUS}`,
        )}
      />
    </div>
  );
}

function UnitCellEditor({
  flow,
  availableUnits,
  disabled,
  onFlowUpdate,
  autoFocus,
}: UnitCellProps & { autoFocus?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null);

  // When this editor mounts because the lazy placeholder was just
  // activated, grab focus synchronously after DOM mutation — the
  // existing `onFocus` handler below opens the dropdown, so the user
  // gets the open searchable combo in one click/tab instead of two.
  // `useLayoutEffect` (not `useEffect` + rAF) so the focus + dropdown
  // open commit lands inside the same React batch that mounted us —
  // matters for `act()`-driven tests that assert against the open
  // dropdown right after `fireEvent.focus`. `firedRef` keeps this
  // strictly first-mount; later renders (e.g. after a modify-mode save
  // flips `disabled`) must NOT re-focus and re-open the dropdown.
  const autoFocusFiredRef = useRef(false);
  useLayoutEffect(() => {
    if (autoFocusFiredRef.current) return;
    if (autoFocus && !disabled) {
      autoFocusFiredRef.current = true;
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled]);

  // Optimistic local override. Proposal-flow updates round-trip through Dexie
  // before `flow.unit_id` catches up; without this the dropdown closes and the
  // cell renders one frame with the old unit, causing a visible flicker.
  // Real flows update the query cache synchronously, so the override clears
  // on the same render and is a no-op for them.
  const [pendingUnitId, setPendingUnitId] = useState<string | null | undefined>(
    undefined,
  );

  const displayedUnitId =
    pendingUnitId === undefined ? (flow.unit_id ?? null) : pendingUnitId;

  // Render-time reset (vs useEffect) — once flow.unit_id catches up to the
  // override, drop it. React discards this render and re-runs with the new
  // state, no commit, no effect tick.
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (pendingUnitId !== undefined && pendingUnitId === (flow.unit_id ?? null)) {
    setPendingUnitId(undefined);
  }

  const resolvedUnit = useMemo(() => {
    if (flow.unit?.id === displayedUnitId) return flow.unit;
    return availableUnits.find((u) => u.id === displayedUnitId) ?? null;
  }, [flow.unit, displayedUnitId, availableUnits]);

  const displayValue = resolvedUnit?.abbreviation || "-";

  const allItems = useMemo(
    () => [
      { id: null as string | null, abbreviation: "-", name: "" },
      ...availableUnits.map((u) => ({
        id: u.id as string | null,
        abbreviation: u.abbreviation || "",
        name: u.name || u.abbreviation || "",
      })),
    ],
    [availableUnits],
  );

  const filteredItems = useMemo(() => {
    if (!hasSearched || !search.trim()) return allItems;
    const s = search.toLowerCase().trim();
    return allItems.filter(
      (item) =>
        item.abbreviation.toLowerCase().startsWith(s) ||
        item.name.toLowerCase().startsWith(s),
    );
  }, [allItems, search, hasSearched]);

  useDropdownPosition(
    open,
    inputRef,
    setDropdownPos,
    UNIT_DROPDOWN_MIN_WIDTH,
    UNIT_DROPDOWN_DESIRED_HEIGHT,
  );

  const selectUnit = useCallback(
    (item: (typeof allItems)[0]) => {
      const unit = item.id
        ? availableUnits.find((u) => u.id === item.id) || null
        : null;
      const updates: Partial<PreOrderFlow> = {
        unit_id: item.id,
        unit: unit,
      };

      // Auto-fill weight from junction weight (product_units.weight)
      const jw = getJunctionWeight(flow.product, unit);
      if (jw != null) {
        if (unit?.per_box) {
          updates.weight = jw;
        } else {
          const qty = flow.quantity || 0;
          if (qty > 0) {
            // Sticky: only overwrite weight if user hasn't edited it
            // (current weight matches the default for the OLD unit at qty).
            const oldDefault = computeDefaultFlowWeight(
              flow.product,
              resolvedUnit,
              qty,
            );
            if (weightsEqual(flow.weight, oldDefault)) {
              updates.weight = qty * jw;
            }
          }
        }
      }

      setPendingUnitId(item.id);
      // Invoke synchronously (callers and tests rely on it). onFlowUpdate's
      // declared return is void, but proposal-flow handlers are actually async
      // and return a Promise — drop the optimistic override on rejection so
      // the cell snaps back to flow.unit_id instead of showing the failed
      // pick forever. Sync throws are caught for the same reason.
      // Identity-check on rollback: if the user picked another unit before
      // this promise settled, leave the newer pendingUnitId alone.
      try {
        const result = onFlowUpdate(flow.id, updates) as unknown;
        if (
          result != null &&
          typeof (result as { then?: unknown }).then === "function"
        ) {
          (result as Promise<unknown>).catch(() =>
            setPendingUnitId((curr) => (curr === item.id ? undefined : curr)),
          );
        }
      } catch {
        setPendingUnitId((curr) => (curr === item.id ? undefined : curr));
      }
      setOpen(false);
      setSearch("");
      setHasSearched(false);
    },
    [
      onFlowUpdate,
      flow.id,
      availableUnits,
      flow.product,
      resolvedUnit,
      flow.weight,
      flow.quantity,
    ],
  );

  const focusNextRowInput = useCallback((current: HTMLElement) => {
    const row = current.closest("tr");
    if (!row) return;
    const inputs = Array.from(
      row.querySelectorAll("input:not([disabled])"),
    ) as HTMLInputElement[];
    const idx = inputs.indexOf(current as HTMLInputElement);
    if (idx >= 0 && idx < inputs.length - 1) {
      inputs[idx + 1].focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        const nextIdx = Math.min(
          highlightedIndex + 1,
          filteredItems.length - 1,
        );
        setHighlightedIndex(nextIdx);
        requestAnimationFrame(() => {
          dropdownRef.current
            ?.querySelector(`[data-unit-index="${nextIdx}"]`)
            ?.scrollIntoView({ block: "nearest" });
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        const prevIdx = Math.max(highlightedIndex - 1, 0);
        setHighlightedIndex(prevIdx);
        requestAnimationFrame(() => {
          dropdownRef.current
            ?.querySelector(`[data-unit-index="${prevIdx}"]`)
            ?.scrollIntoView({ block: "nearest" });
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (open && filteredItems[highlightedIndex]) {
          selectUnit(filteredItems[highlightedIndex]);
        }
        // Shift+Enter → add row (last line); plain Enter → next row's unit
        // cell, falling back to the add row on the last data row.
        focusEnterTarget(e.currentTarget, e.shiftKey);
      } else if (e.key === "Tab") {
        if (open && filteredItems[highlightedIndex]) {
          selectUnit(filteredItems[highlightedIndex]);
        }
        const moved = e.shiftKey
          ? focusPrevTabCell(e.currentTarget)
          : focusNextTabCell(e.currentTarget);
        if (moved) {
          e.preventDefault();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        inputRef.current?.blur();
      } else if (e.key === " " && !hasSearched) {
        e.preventDefault();
        const currentIdx = allItems.findIndex(
          (item) => item.id === displayedUnitId,
        );
        const nextIdx = (currentIdx + 1) % allItems.length;
        selectUnit(allItems[nextIdx]);
      }
    },
    [
      open,
      filteredItems,
      highlightedIndex,
      selectUnit,
      hasSearched,
      allItems,
      displayedUnitId,
    ],
  );

  const dropdownContent =
    open && filteredItems.length > 0 && dropdownPos ? (
      <div
        ref={dropdownRef}
        data-recap-portal
        style={{
          position: "fixed",
          zIndex: 9999,
          ...dropdownPanelStyle(dropdownPos),
        }}
        className="bg-white border border-gray-200 rounded-md shadow-lg overflow-auto"
      >
        {filteredItems.map((item, index) => (
          <div
            key={item.id ?? "none"}
            data-unit-index={index}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              selectUnit(item);
              focusNextRowInput(inputRef.current!);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
            className={cn(
              "px-2 py-1.5 text-sm cursor-pointer text-center",
              index === highlightedIndex
                ? "bg-blue-50 text-blue-900"
                : "hover:bg-gray-50",
              item.id === null && "text-gray-500",
            )}
          >
            {item.abbreviation}
            {item.name && item.name !== item.abbreviation && (
              <span className="ml-1 text-xs text-gray-400">{item.name}</span>
            )}
          </div>
        ))}
      </div>
    ) : open && filteredItems.length === 0 && hasSearched && dropdownPos ? (
      <div
        data-recap-portal
        style={{
          position: "fixed",
          zIndex: 9999,
          ...dropdownPanelStyle(dropdownPos),
        }}
        className="bg-white border border-gray-200 rounded-md shadow-lg p-2 text-xs text-gray-500 text-center"
      >
        -
      </div>
    ) : null;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        data-tab-cell
        value={open ? search : displayValue}
        onChange={(e) => {
          setSearch(e.target.value);
          setHasSearched(true);
          setOpen(true);
          setHighlightedIndex(0);
        }}
        onFocus={() => {
          setSearch("");
          setHasSearched(false);
          setOpen(true);
          const currentIdx = allItems.findIndex(
            (item) => item.id === displayedUnitId,
          );
          setHighlightedIndex(currentIdx >= 0 ? currentIdx : 0);
          requestAnimationFrame(() => inputRef.current?.select());
        }}
        onBlur={() => {
          setOpen(false);
          setSearch("");
          setHasSearched(false);
        }}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        className={cn(
          CELL_INPUT_SELECT,
          resolvedUnit?.abbreviation ? "text-gray-900" : "text-gray-400",
          disabled
            ? CELL_INPUT_DISABLED
            : `cursor-pointer ${CELL_INPUT_SELECT_FOCUS}`,
        )}
      />
      {typeof window !== "undefined" &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
}
